"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Compare_1 = require("../Utility/Compare");
class Objects {
    /**
     * Recursively merge source into target, overwriting any values of target if necessary
     */
    static merge(target, source) {
        // Iterate through `source` properties
        for (const key of Object.keys(source)) {
            let targetValue = target[key];
            if (Array.isArray(source[key])) {
                targetValue = targetValue === undefined ? [] : targetValue;
                if (Array.isArray(targetValue)) {
                    target[key] = targetValue.concat(source[key]);
                }
                else {
                    throw new Error('Unable to assign: ' + key + '. Source value is array, target value is not');
                }
            }
            else if (Objects.isJSONObject(source[key])) {
                // convert array to object
                if (Array.isArray(targetValue)) {
                    targetValue = { ...targetValue };
                }
                // deep merge objects
                if (targetValue === undefined || Objects.isJSONObject(targetValue)) {
                    target[key] = Objects.merge(targetValue !== null && targetValue !== void 0 ? targetValue : {}, source[key]);
                }
                else {
                    throw new Error('Unable to assign: ' + key + '. Source value is object, target value is not');
                }
            }
            else {
                // anything but obj or array, can safely assign
                target[key] = source[key];
            }
        }
        return target;
    }
    /**
     * Assign `key` + `value` to `data`. Take nested key notation into account
     *
     * 3 examples:
     *  <input name="brand" value="1">                - single
     *  <input name="product[2145]" value="2">        - keyed
     *  <input name="makes[]" value = "3">            - multi
     *
     * End result after treatment of each element:
     * {
     *     "brand": "1",
     *     "product": {"2145": "2"},
     *     "makes": ['3']
     * }
     */
    static assign(data, key, value) {
        // if key doesn't have [, just assign
        if (key.includes('[') === false) {
            data[key] = value;
            return data;
        }
        // split `name[a][]` notation into `['name', 'a', '']`
        const names = key.replace(/ /g, '').split('[').map((val) => (val.endsWith(']') ? val.slice(0, -1) : val));
        // create nested object based on notation and assign value
        let target = value;
        names.reverse().forEach((name) => {
            if (name === '') {
                target = [target];
            }
            else {
                const obj = {};
                obj[name] = target;
                target = obj;
            }
        });
        // merge nested object with data
        return Objects.merge(data, target);
    }
    /**
     * Filter any "undefined' values from the string map (recursively)
     */
    static filter(data) {
        Object.keys(data).forEach((key) => {
            if (data[key] === undefined) {
                delete data[key];
            }
            else if (Objects.isJSONObject(data[key])) {
                data[key] = Objects.filter(data[key]);
            }
        });
        return data;
    }
    /**
     * Convert multidimensional object to single dimension (recursively), keeping the keys
     *
     * {"a": {"b": {"c": "d"}}}  =>  {'a[b][c]': "d"}
     */
    static flatten(object, prefix = null, output = {}) {
        Object.keys(object).forEach((key) => {
            // increase the length of the prefix the deeper we go
            const name = prefix ? prefix + `[${key}]` : key;
            if (Array.isArray(object[key]) === false && object[key] instanceof Object) {
                // value is object, recursively assign
                Objects.flatten(object[key], name, output);
            }
            else {
                // otherwise just assign
                output[name] = object[key];
            }
        });
        return output;
    }
    /**
     * Deep compare two data objects, regardless of internal ordering
     */
    static deepEquals(left, right) {
        if (left === right) {
            return true;
        }
        const leftKeys = Object.keys(left);
        const rightKeys = Object.keys(right);
        if (leftKeys.length !== rightKeys.length) {
            return false;
        }
        for (const key of leftKeys) {
            if ((0, Compare_1.default)(left[key], right[key]) === false) {
                return false;
            }
        }
        return true;
    }
    /**
     * Anything in javascript is an Object. Date, File, String, Array, ... This method tests specifically for JSON object.
     *
     * @note suppressing eslint warnings to have consistent behaviour in native javascript
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    static isJSONObject(value) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return (value === null || value === void 0 ? void 0 : value.constructor) === Objects.OBJ_CONSTRUCTOR;
    }
    /**
     * Implode key/value pair object into string.
     *
     * @param object the object to implode
     * @param glue default is '='
     * @param separator default is ','
     */
    static implode(object, glue, separator) {
        const result = [];
        Object.keys(object).forEach(key => result.push(key + (glue !== null && glue !== void 0 ? glue : '=') + object[key]));
        return result.join(separator !== null && separator !== void 0 ? separator : ',');
    }
}
Objects.OBJ_CONSTRUCTOR = {}.constructor;
exports.default = Objects;
//# sourceMappingURL=Objects.js.map